import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BookMarked,
  Upload,
  Trash2,
  RefreshCw,
  FileText,
  AlertCircle,
  Loader2,
  Plus,
  Repeat,
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import { useTenant } from '../../contexts/TenantContext';

interface StandardDoc {
  id: number;
  original_name: string;
  file_type: string;
  file_size: number;
  description: string | null;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  is_enabled: boolean;
  client_tag_id: number | null;
  created_at: string;
}

interface ClientTagOption {
  tag_id: number;
  tag_name: string;
}

const MAX_STANDARDS_PER_SCOPE = 10;

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusBadge = (status: StandardDoc['processing_status']) => {
  const map: Record<StandardDoc['processing_status'], string> = {
    completed: 'bg-green-100 text-green-800',
    processing: 'bg-blue-100 text-blue-800',
    pending: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status]}`}>
      {status}
    </span>
  );
};

interface TenantOption {
  id: number;
  name: string;
  subdomain: string;
}

const OrgStandardsLibraryAdmin: React.FC = () => {
  const { tenantData, tenantInfo, isLoading: tenantLoading } = useTenant();
  // On a tenant subdomain the ambient tenant drives everything (unchanged behavior).
  // On the main Admin domain tenantData is intentionally null, so the admin picks a
  // tenant explicitly — without this the doc fetch early-returned and the loading
  // spinner never cleared.
  const onMainDomain = tenantInfo.isMainDomain;
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(onMainDomain);
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const tenantId = onMainDomain ? selectedTenantId : tenantData?.id;

  const [docs, setDocs] = useState<StandardDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [replacingId, setReplacingId] = useState<number | null>(null);
  const [scopingId, setScopingId] = useState<number | null>(null);

  // Styled in-app confirmation (replaces window.confirm — native dialogs also freeze
  // automated QA sessions, same reason the Subworkstream delete confirm was replaced).
  const [confirmAction, setConfirmAction] = useState<
    | { kind: 'delete'; doc: StandardDoc }
    | { kind: 'replace'; doc: StandardDoc; file: File }
    | null
  >(null);

  // Client scoping (P1): null client_tag_id = tenant-wide, set = client-only.
  const [clientTags, setClientTags] = useState<ClientTagOption[]>([]);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'tenant' | number>('all');

  const addInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const fetchDocs = useCallback(async () => {
    if (!tenantId) {
      // No tenant resolved (main domain before a tenant is picked) — settle the
      // loading state instead of leaving the spinner up forever.
      setDocs([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/tenants/${tenantId}/documents`);
      setDocs(response.data.documents || []);
    } catch (err: any) {
      console.error('Error fetching organization standards:', err);
      setError(err.response?.data?.error || 'Failed to load the standards library');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/api/client-tags');
        if (!cancelled) {
          setClientTags((response.data.tags || []).map((t: any) => ({ tag_id: t.tag_id, tag_name: t.tag_name })));
        }
      } catch (err) {
        // Non-fatal: scope column falls back to showing "Tenant-wide" only.
        console.error('Error fetching client tags for standards library:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!onMainDomain) return;
    let cancelled = false;
    (async () => {
      try {
        setTenantsLoading(true);
        setTenantsError(null);
        // Lightweight names-only endpoint — the /enhanced and legacy GET /api/tenants
        // listings join users/projects per tenant and can hit the DB statement timeout.
        const response = await api.get('/api/tenants/options');
        if (cancelled) return;
        const rows: TenantOption[] = (response.data.tenants || []).map((t: any) => ({
          id: t.id,
          name: t.name,
          subdomain: t.subdomain,
        }));
        setTenants(rows);
        if (rows.length === 1) setSelectedTenantId(rows[0].id);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Error fetching tenants for standards library:', err);
        setTenantsError(
          err.response?.status === 403
            ? 'Only Super Admins can browse tenant standards from the main domain.'
            : err.response?.data?.error || 'Failed to load the tenant list'
        );
      } finally {
        if (!cancelled) setTenantsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onMainDomain]);

  const uploadStandard = async (
    file: File,
    description: string | null,
    clientTagId: number | null = null,
  ) => {
    const formData = new FormData();
    formData.append('document', file);
    if (description) formData.append('description', description);
    // Absent / empty = tenant-wide; integer = client-specific scope.
    if (clientTagId !== null) formData.append('client_tag_id', String(clientTagId));
    await api.post(`/api/tenants/${tenantId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  // Destination scope for new uploads follows the active filter so "Pacific Life only"
  // + Add Standard lands in that scope. "All scopes" defaults to tenant-wide.
  const uploadScopeId: number | null =
    typeof scopeFilter === 'number' ? scopeFilter : null;

  const countInScope = (clientTagId: number | null) =>
    docs.filter(d => (d.client_tag_id ?? null) === clientTagId).length;

  const scopeUsedCount = countInScope(uploadScopeId);
  const scopeAtLimit = scopeUsedCount >= MAX_STANDARDS_PER_SCOPE;

  const scopeLabelFor = (clientTagId: number | null): string => {
    if (clientTagId === null) return 'Tenant-wide';
    return clientTags.find(t => t.tag_id === clientTagId)?.tag_name || 'this scope';
  };

  const handleAddChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !tenantId) return;

    if (scopeAtLimit) {
      toast.error(
        `Standards scope limit reached (max ${MAX_STANDARDS_PER_SCOPE} in ${scopeLabelFor(uploadScopeId)}). Remove a standard from this scope before adding another.`,
      );
      return;
    }

    try {
      setUploading(true);
      await uploadStandard(file, null, uploadScopeId);
      const scopeNote =
        uploadScopeId === null
          ? ' (tenant-wide)'
          : ` (scoped to ${scopeLabelFor(uploadScopeId)})`;
      toast.success(`"${file.name}" added to the Organization Standards Library${scopeNote}`);
      await fetchDocs();
    } catch (err: any) {
      console.error('Error uploading standard:', err);
      toast.error(err.response?.data?.error || 'Failed to upload the standard');
    } finally {
      setUploading(false);
    }
  };

  const handleReplaceClick = (doc: StandardDoc) => {
    setReplacingId(doc.id);
    // Defer to next tick so replacingId is set before the native picker opens.
    setTimeout(() => replaceInputRef.current?.click(), 0);
  };

  const handleReplaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const targetId = replacingId;
    setReplacingId(null);
    if (!file || !targetId || !tenantId) return;

    const target = docs.find(d => d.id === targetId);
    if (!target) return;

    setConfirmAction({ kind: 'replace', doc: target, file });
  };

  const performReplace = async (target: StandardDoc, file: File) => {
    try {
      setUploading(true);
      // Delete first so a replace never trips the per-scope max.
      await api.delete(`/api/tenants/${tenantId}/documents/${target.id}`);
      await uploadStandard(file, target.description, target.client_tag_id ?? null);
      toast.success(`Replaced "${target.original_name}" with "${file.name}"`);
      await fetchDocs();
    } catch (err: any) {
      console.error('Error replacing standard:', err);
      toast.error(err.response?.data?.error || 'Failed to replace the standard');
      await fetchDocs();
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (doc: StandardDoc) => {
    if (!tenantId) return;
    setConfirmAction({ kind: 'delete', doc });
  };

  const performDelete = async (doc: StandardDoc) => {
    try {
      setDeletingId(doc.id);
      await api.delete(`/api/tenants/${tenantId}/documents/${doc.id}`);
      toast.success(`"${doc.original_name}" removed`);
      setDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch (err: any) {
      console.error('Error removing standard:', err);
      toast.error(err.response?.data?.error || 'Failed to remove the standard');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = async (doc: StandardDoc) => {
    if (!tenantId) return;
    const nextEnabled = !doc.is_enabled;
    try {
      setTogglingId(doc.id);
      await api.patch(`/api/tenants/${tenantId}/documents/${doc.id}`, { is_enabled: nextEnabled });
      setDocs(prev => prev.map(d => (d.id === doc.id ? { ...d, is_enabled: nextEnabled } : d)));
      toast.success(`"${doc.original_name}" ${nextEnabled ? 'enabled' : 'disabled'} organization-wide`);
    } catch (err: any) {
      console.error('Error toggling standard:', err);
      toast.error(err.response?.data?.error || 'Failed to update the standard');
    } finally {
      setTogglingId(null);
    }
  };

  const handleScopeChange = async (doc: StandardDoc, newTagId: number | null) => {
    if (!tenantId || newTagId === doc.client_tag_id) return;
    const tagName = newTagId ? clientTags.find(t => t.tag_id === newTagId)?.tag_name : null;
    try {
      setScopingId(doc.id);
      await api.patch(`/api/tenants/${tenantId}/documents/${doc.id}`, { client_tag_id: newTagId });
      setDocs(prev => prev.map(d => (d.id === doc.id ? { ...d, client_tag_id: newTagId } : d)));
      toast.success(
        newTagId
          ? `"${doc.original_name}" is now scoped to ${tagName} only`
          : `"${doc.original_name}" is now tenant-wide (all clients)`
      );
    } catch (err: any) {
      console.error('Error changing standard scope:', err);
      toast.error(err.response?.data?.error || 'Failed to change the standard’s scope');
    } finally {
      setScopingId(null);
    }
  };

  const visibleDocs = docs.filter(d => {
    if (scopeFilter === 'all') return true;
    if (scopeFilter === 'tenant') return d.client_tag_id === null;
    return d.client_tag_id === scopeFilter;
  });

  if (tenantLoading || tenantsLoading || (!!tenantId && loading && docs.length === 0 && !error)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BookMarked className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Organization Standards Library</h2>
            <p className="text-gray-600 text-sm mt-1">
              These documents are automatically available to SOP generation runs. A standard's Scope controls
              who gets it: <strong>Tenant-wide</strong> applies to every project on the tenant, while a
              client-specific scope injects it only into that client's projects (e.g. Pacific Life standards
              never reach an American Express project). Use the toggle to disable a standard without deleting
              it. Users can additionally exclude any enabled standard per project from the Add Documents
              screen in SOP Builder.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={fetchDocs}
            disabled={loading}
            title="Refresh"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => addInputRef.current?.click()}
            disabled={uploading || !tenantId || scopeAtLimit}
            title={
              scopeAtLimit
                ? `Limit reached for ${scopeLabelFor(uploadScopeId)} (max ${MAX_STANDARDS_PER_SCOPE})`
                : scopeFilter === 'all'
                  ? 'Adds as tenant-wide — switch the scope filter to land in a client scope'
                  : `Add to ${scopeLabelFor(uploadScopeId)}`
            }
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span>Add Standard</span>
          </button>
        </div>
      </div>

      {onMainDomain && (
        <div className="mt-4 flex items-center space-x-3">
          <label htmlFor="standards-tenant-select" className="text-sm font-medium text-gray-700 flex-shrink-0">
            Tenant
          </label>
          <select
            id="standards-tenant-select"
            data-testid="standards-tenant-select"
            value={selectedTenantId ?? ''}
            onChange={(e) => {
              setDocs([]);
              setError(null);
              setSelectedTenantId(e.target.value ? Number(e.target.value) : null);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-xs w-full"
          >
            <option value="">Select a tenant…</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.subdomain})
              </option>
            ))}
          </select>
          {!!tenantId && clientTags.length > 0 && (
            <select
              data-testid="standards-scope-filter"
              value={scopeFilter === 'all' ? 'all' : scopeFilter === 'tenant' ? 'tenant' : String(scopeFilter)}
              onChange={(e) => {
                const v = e.target.value;
                setScopeFilter(v === 'all' ? 'all' : v === 'tenant' ? 'tenant' : Number(v));
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Filter by scope"
            >
              <option value="all">All scopes</option>
              <option value="tenant">Tenant-wide only</option>
              {clientTags.map(t => (
                <option key={t.tag_id} value={t.tag_id}>{t.tag_name} only</option>
              ))}
            </select>
          )}
        </div>
      )}

      <input
        ref={addInputRef}
        type="file"
        className="hidden"
        accept=".csv,.pdf,.docx,.doc,.xlsx,.xls,.txt,.md"
        onChange={handleAddChange}
      />
      <input
        ref={replaceInputRef}
        type="file"
        className="hidden"
        accept=".csv,.pdf,.docx,.doc,.xlsx,.xls,.txt,.md"
        onChange={handleReplaceChange}
      />

      {tenantsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 flex items-start space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 text-sm">{tenantsError}</p>
        </div>
      ) : onMainDomain && !tenantId ? (
        <div className="mt-4 border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
          <BookMarked className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">Select a tenant to view its Standards Library.</p>
          <p className="text-xs mt-1">Standards are stored per tenant — pick one above to see and manage its documents.</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 flex items-start space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 text-sm">{error}</p>
            <button onClick={fetchDocs} className="text-red-700 text-sm underline mt-1">Retry</button>
          </div>
        </div>
      ) : docs.length === 0 ? (
        <div className="mt-4 border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
          <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No standards in the library yet.</p>
          <p className="text-xs mt-1">Click "Add Standard" to upload the first one.</p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Standard</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Size</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Status</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Scope</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Enabled</th>
                <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocs.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 px-3 text-sm text-gray-500 text-center">
                    No standards match this scope filter.
                  </td>
                </tr>
              )}
              {visibleDocs.map(doc => (
                <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-3 text-sm font-medium text-gray-900">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span>{doc.original_name}</span>
                    </div>
                    {doc.description && (
                      <p className="text-xs text-gray-500 mt-0.5 ml-6">{doc.description}</p>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm text-gray-600">{formatBytes(doc.file_size)}</td>
                  <td className="py-3 px-3">{statusBadge(doc.processing_status)}</td>
                  <td className="py-3 px-3">
                    <select
                      value={doc.client_tag_id ?? ''}
                      disabled={scopingId === doc.id}
                      onChange={(e) => handleScopeChange(doc, e.target.value ? Number(e.target.value) : null)}
                      className={`border rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                        doc.client_tag_id ? 'border-purple-300 text-purple-800' : 'border-gray-300 text-gray-700'
                      }`}
                      title={doc.client_tag_id ? 'Client-specific: only injected into this client’s projects' : 'Tenant-wide: injected into every client’s projects on this tenant'}
                    >
                      <option value="">Tenant-wide</option>
                      {clientTags.map(t => (
                        <option key={t.tag_id} value={t.tag_id}>{t.tag_name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 px-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={doc.is_enabled}
                        disabled={togglingId === doc.id}
                        onChange={() => handleToggle(doc)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center justify-end space-x-1">
                      <button
                        onClick={() => handleReplaceClick(doc)}
                        disabled={uploading || deletingId === doc.id}
                        title="Replace file"
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                      >
                        {replacingId === doc.id && uploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Repeat className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        disabled={deletingId === doc.id}
                        title="Remove"
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      >
                        {deletingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <span>
          {tenantId
            ? scopeFilter === 'all'
              ? `${docs.length} total · max ${MAX_STANDARDS_PER_SCOPE} per scope`
              : `${scopeUsedCount} of ${MAX_STANDARDS_PER_SCOPE} used in ${scopeLabelFor(uploadScopeId)}`
            : ''}
        </span>
        <span className="flex items-center space-x-1">
          <Upload className="h-3 w-3" />
          <span>PDF, Word, Excel, CSV, text, or Markdown — up to 100MB each</span>
        </span>
      </div>

      {confirmAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmAction(null)}
        >
          <div
            data-testid="standards-confirm-dialog"
            className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg flex-shrink-0 ${confirmAction.kind === 'delete' ? 'bg-red-100' : 'bg-blue-100'}`}>
                {confirmAction.kind === 'delete' ? (
                  <Trash2 className="h-5 w-5 text-red-600" />
                ) : (
                  <Repeat className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">
                  {confirmAction.kind === 'delete' ? 'Remove this standard?' : 'Replace this standard?'}
                </h3>
                <p className="text-sm text-gray-600 mt-1 break-words">
                  {confirmAction.kind === 'delete' ? (
                    <>
                      <span className="font-medium text-gray-900">"{confirmAction.doc.original_name}"</span> will be
                      removed from the Organization Standards Library and will no longer be included in any
                      project's SOP generation.
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-gray-900">"{confirmAction.doc.original_name}"</span> will be
                      replaced with <span className="font-medium text-gray-900">"{confirmAction.file.name}"</span>.
                      The current file is removed and the new one uploads in its place.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <button
                data-testid="standards-confirm-cancel"
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                data-testid="standards-confirm-ok"
                onClick={() => {
                  const action = confirmAction;
                  setConfirmAction(null);
                  if (action.kind === 'delete') performDelete(action.doc);
                  else performReplace(action.doc, action.file);
                }}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  confirmAction.kind === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmAction.kind === 'delete' ? 'Remove' : 'Replace'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgStandardsLibraryAdmin;
